/* src/modules/interviews/selectors.js */

import { createSelector } from 'reselect';
import { filter, forEach, groupBy, includes, isEmpty, keyBy, map, mapValues, omit } from 'lodash';
import { getMoment } from '../../util/dates';

const convert = m => m.minutes() + m.hours() * 60;
const omitTypename = array => map(array, a => omit(a, '__typename'));

export const getSchedule = createSelector(
  interviews => Object.values(interviews.schedule.data),
  interviews => interviews.interviews.data,
  (schedule, byID) => {
    const combined = {};
    if (isEmpty(schedule)) return combined;
    const statuses = ['CONFIRMED', 'R_CONFIRMED'];
    forEach(schedule, ({ user: { _id, first, last }, timeblocks }) => {
      forEach(timeblocks, ({ _id: t, date, startTime, endTime, pattern }) => {
        const start = getMoment([date.timestamp, startTime.timestamp]);
        const end = getMoment([date.timestamp, endTime.timestamp]);
        const key = start.format('MMM-D-YYYY');
        if (isEmpty(combined[key])) combined[key] = { availability: [], interviews: [] };
        combined[key].availability.push({ _id: t, pattern, start, end, owner: { _id, name: `${first} ${last}` } });
      });
    });
    forEach(combined, v => v.availability.sort((a, b) => a.start.valueOf() - b.start.valueOf()));
    forEach(
      filter(Object.values(byID), obj => includes(statuses, obj.status)),
      ({ current: { startTime } }) => {
        const d = getMoment(startTime.timestamp);
        const key = d.format('MMM-D-YYYY');
        const current = (combined[key] || { interviews: [] }).interviews;
        if (!combined[key]) combined[key] = { interviews: [] };
        combined[key].interviews = [...(current || []), d];
        forEach(combined[key].availability, ({ start, end, interviews, ...rest }, i) => {
          if (convert(d) >= convert(start) && convert(d) <= convert(end)) {
            combined[key].availability[i] = {
              ...rest,
              start,
              end,
              interviews: [...(interviews || []), d],
            };
          }
        });
      }
    );
    return combined;
  }
);

export const getOrganizations = createSelector(
  auth => auth.data.user,
  user => map(user.organizations, o => o._id)
);

export const getUniversities = createSelector(
  auth => auth.data.user,
  user => map(user.interview_universities, o => o._id)
);

export const getLocations = createSelector(
  interview => interview.locations.data,
  locations => map(Object.values(locations), l => omit(l, ['__typename', 'city', 'coordinates']))
);

const formatInterviews = (byID, statuses, type) => map(
  filter(Object.values(byID), ({ status }) => includes(statuses, status)),
  interview => {
    const { tutor: { _id: tutorID, user: { first, last } }, _id, location, status } = interview;
    const { startTime: { timestamp } } = interview[type];
    const time = getMoment(timestamp);
    return {
      fields: {
        _id,
        name: `${first} ${last}`,
        'Date/Time': time.format('MMM D, YYYY, HH:mm'),
        location: location.name,
      },
      status,
      user: { _id: tutorID },
    };
  }
);

export const getInterviewRequests = createSelector(
  state => state.interviews.interviews.data,
  byID => formatInterviews(byID, ['PENDING', 'R_PENDING'], 'request')
);

export const getUpcomingInterviews = createSelector(
  state => state.interviews.interviews.data,
  byID => map(formatInterviews(byID, ['CONFIRMED'], 'current'), i => i.fields)
);

export const getAppliedCourses = createSelector(
  state => state.interviews.applicant.data,
  data => {
    if (!data) return {};
    return mapValues(
      groupBy(data.courses, c => c.course.university.name),
      courseObj => map(courseObj, c => ({
        code: `${c.course.subject} ${c.course.number}`,
        title: c.course.title,
      }))
    );
  }
);

export const getQuestions = createSelector(
  state => state.interviews.questions.data,
  questions => omitTypename(Object.values(questions))
);

export const getApplicantQuestions = createSelector(
  state => state.interviews.questions.data,
  state => state.interviews.applicant.data,
  (questions, applicantData) => {
    if (!applicantData) return map(questions, q => ({ ...q, answer: '' }));
    const { applicant: { qAndA } } = applicantData;
    const result = [];
    const omitList = [];
    const keyedQAndA = keyBy(qAndA, 'referenceID');
    forEach(questions, ({ _id, text }) => {
      let ans = '';
      if (keyedQAndA[_id]) {
        ans = keyedQAndA[_id].answer;
        omitList.push(_id);
      }
      result.push({ _id, text, answer: ans });
    });
    forEach(omit(keyedQAndA, omitList), ({ referenceID, question, answer }) => {
      if (answer !== '') result.push({ _id: referenceID, text: question, answer });
    });
    return result;
  }
);

export const getRatings = createSelector(
  state => state.interviews.applicant.data,
  applicantData => {
    const empty = { knowledge: 1, organization: 1, helpfulness: 1 };
    if (!applicantData) return empty;
    return (applicantData.applicant || { ratings: empty }).ratings;
  }
);

export const getApplicantID = createSelector(
  state => state.interviews.applicant.data,
  applicantData => {
    if (!applicantData) return '';
    return applicantData.applicant._id;
  }
);

export const getNotesList = createSelector(
  state => state.interviews.applicant.data,
  applicant => {
    if (isEmpty(applicant)) return [];
    const notes = map(applicant.user.notes, n => {
      const { _id, admin: { user }, body, createdAt } = n;
      const date = getMoment(createdAt.timestamp);
      return {
        _id,
        author: `${user.first} ${user.last}`,
        body,
        date,
      };
    });
    notes.sort((a, b) => a.date.valueOf() - b.date.valueOf());
    return map(notes, ({ date, ...rest }) => ({
      ...rest,
      date: date.format('MMM D, Y'),
    }));
  }
);



// WEBPACK FOOTER //
// ./src/modules/interviews/selectors.js